import { Helmet } from "react-helmet-async";
import Screen from "../Layouts/Screen/Screen";
import { useEffect, useRef, useState } from "react";
import { Save } from "react-feather";
import { useAuth, useMessage } from "../../global-components/src";
import { errorMessage } from "../../global-components/src/utils/text";
import { api } from "../../services/api";
import Spinner from "../../global-components/src/components/spinner/spiner";
import "../../components/Modals/frames-options/frames-options.css";

const ChatQuickRepliesSettings = () => {
    const { token, user } = useAuth();
    const { setMessage } = useMessage();

    const [enabled, setEnabled] = useState(false);
    const [reply, setReply] = useState("");
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isBlack = user?.stamps?.includes("black");

    useEffect(() => {
        if (!isBlack) return;
        setLoading(true);
        api(token)
            .get("/user/chat-quick-reply")
            .then((res) => {
                setEnabled(res.data.enabled);
                setReply(res.data.reply ?? "");
                setDraft(res.data.reply ?? "");
            })
            .catch((err) => setMessage(errorMessage(err), "error"))
            .finally(() => setLoading(false));
    }, [user?.id]);

    const handleToggle = async () => {
        const next = !enabled;
        setEnabled(next);
        try {
            await api(token).put("/user/chat-quick-reply/toggle", { enabled: next });
        } catch (err) {
            setEnabled(!next);
            setMessage(errorMessage(err), "error");
        }
    };

    const handleSave = async () => {
        const msg = draft.trim();
        if (!msg) return;
        setSaving(true);
        try {
            const res = await api(token).put("/user/chat-quick-reply", { reply: msg });
            setReply(res.data.reply);
            setDraft(res.data.reply);
            setMessage("Resposta rápida salva.", "success");
        } catch (err) {
            setMessage(errorMessage(err), "error");
        } finally {
            setSaving(false);
        }
    };

    if (!isBlack) {
        return (
            <Screen paddingTopTitle title="Respostas Rápidas">
                <div className="foc-body">
                    <div className="foc-error-box" style={{ marginTop: 0 }}>
                        Este recurso é exclusivo para usuários com selo <strong>black</strong>.
                    </div>
                </div>
            </Screen>
        );
    }

    return (
        <>
            <Screen paddingTopTitle title="Resposta Rápida">
                <Helmet>
                    <title>Paçoca - Resposta Rápida do Chat</title>
                    <meta name="description" content="Configure uma resposta rápida automática para mensagens no chat." />
                </Helmet>

                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                        <Spinner />
                    </div>
                ) : (
                    <div className="foc-body">

                        {/* ── Toggle ──────────────────────────────────────── */}
                        <div
                            className="foc-price-row"
                            style={{ cursor: "pointer", userSelect: "none", marginBottom: 20 }}
                            onClick={handleToggle}
                        >
                            <div>
                                <div className="foc-pay-title">Resposta automática</div>
                                <div className="foc-pay-sub" style={{ marginTop: 2 }}>
                                    Quem te mandar a 1ª mensagem do dia recebe esta resposta.
                                    O chat nunca cai em solicitação.
                                </div>
                            </div>

                            {/* Toggle pill */}
                            <div
                                style={{
                                    width: 44,
                                    height: 24,
                                    borderRadius: 12,
                                    background: enabled ? "var(--foc-brown)" : "rgba(0,0,0,0.15)",
                                    position: "relative",
                                    flexShrink: 0,
                                    marginLeft: 14,
                                    transition: "background 0.2s",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 3,
                                        left: enabled ? 22 : 3,
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        background: "#fff",
                                        transition: "left 0.2s",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                    }}
                                />
                            </div>
                        </div>

                        {/* ── Section label ───────────────────────────────── */}
                        <div className="foc-section-label">mensagem de resposta</div>

                        {/* ── Textarea ────────────────────────────────────── */}
                        <div className="foc-key-wrap" style={{ marginTop: 0 }}>
                            <textarea
                                ref={textareaRef}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Ex: Oi! Obrigado pela mensagem, vou responder em breve 😊"
                                maxLength={500}
                                rows={4}
                                className="foc-key-input"
                                style={{
                                    resize: "vertical",
                                    fontFamily: "inherit",
                                    letterSpacing: "normal",
                                }}
                            />
                            <div className="foc-pay-sub" style={{ textAlign: "right", marginTop: 4 }}>
                                {draft.length}/500
                            </div>
                        </div>

                        {/* ── Actions ─────────────────────────────────────── */}
                        <div className="foc-actions foc-actions-col">
                            <button
                                type="button"
                                className="foc-btn foc-btn-primary"
                                onClick={handleSave}
                                disabled={saving || !draft.trim() || draft.trim() === reply}
                            >
                                {saving ? <Spinner /> : <><Save size={15} /> Salvar resposta</>}
                            </button>
                        </div>

                    </div>
                )}
            </Screen>
        </>
    );
};

export default ChatQuickRepliesSettings;
